<template>
    <v-container fluid class="py-8 px-10 bg-background">
        <BookingStepper :active-step="2" class="mb-6" />
        <FullPageLoader :visible="isLoading" />

        <v-snackbar v-model="snackbar.show" :color="snackbar.color" location="top center" timeout="3000">
            {{ snackbar.msg }}
        </v-snackbar>

        <v-row>
            <!-- RIGHT: Listings -->
            <v-col cols="12" md="12" v-if="cruise" max-width="80%">
                <div class="d-flex align-center justify-space-between mb-3 flex-wrap">
                    <!-- Left: title + subtitle -->
                    <div class="d-flex flex-column mr-4">
                        <h3 class="text-h6 font-weight-bold m-0">{{ selectedListing?.itinerary.title }}</h3>
                        <span class="text-overline text-medium-emphasis">{{
                            selectedListing?.itinerary.ship_name }}</span>
                    </div>

                    <!-- Right: button -->
                    <v-btn variant="tonal" prepend-icon="mdi-ferry" @click="showPorts = true">
                        Ports & Itinerary
                    </v-btn>
                </div>
                <v-col cols="12" class="mb-4">
                    <v-sheet class="summary-header" rounded="lg" elevation="1">
                        <!-- Two-column grid: left = content (padded), right = price (no padding) -->
                        <v-row align="stretch" no-gutters>
                            <!-- LEFT CONTENT (add padding here only) -->
                            <v-col cols="12" md="10" class="px-6 py-5">
                                <v-row align="center" class="flex-wrap">
                                    <!-- Embark -->
                                    <v-col cols="12" md="2" class="d-flex align-center">
                                        <v-icon size="40" class="mr-4">mdi-ferry</v-icon>
                                        <div>
                                            <div class="text-h6 text-primary font-weight-bold">
                                                {{ formatSailingDate(startDateObj) }}
                                            </div>
                                            <div class="text-body-2"><b>Embark:</b></div>
                                            <div class="text-caption">
                                                {{ selectedListing?.itinerary.ports[0].port || '—' }}
                                            </div>
                                        </div>
                                    </v-col>

                                    <!-- Timeline -->
                                    <v-col cols="12" md="2" class="d-flex flex-column align-center text-center">
                                        <div class="d-flex align-center w-100 justify-center">
                                            <div class="timeline-dot"></div>
                                            <div class="timeline-line mx-3"></div>
                                            <div class="timeline-dot"></div>
                                        </div>
                                        <div class="text-caption mt-1">
                                            Cruise<br />
                                            <b>for {{ nightsText }}</b>
                                        </div>
                                    </v-col>

                                    <!-- Disembark -->
                                    <v-col cols="12" md="2" class="d-flex justify-end">
                                        <div class="text-right">
                                            <div class="text-h6 text-primary font-weight-bold">
                                                {{ formatSailingDate(endDateObj) }}
                                            </div>
                                            <div class="text-body-2"><b>Disembark:</b></div>
                                            <div class="text-caption">
                                                {{
                                                    selectedListing?.itinerary.ports[
                                                        selectedListing?.itinerary.ports.length - 1
                                                    ]?.port || '—'
                                                }}
                                            </div>
                                        </div>
                                    </v-col>

                                    <!-- Info strip -->
                                    <v-col cols="12" md="4" class="d-flex flex-column flex-md-row">
                                        <div class="py-2 px-3 border-right-md flex-grow-1">
                                            <span class="font-weight-bold">Passengers:</span>
                                            <v-icon size="16" class="mx-1">mdi-account-multiple</v-icon>
                                            {{ guests.length }}
                                        </div>
                                        <div class="py-2 px-3 border-right-md flex-grow-1">
                                            <span class="font-weight-bold">Category:</span>
                                            <span class="ml-1">{{ selectedListing?.room_type || draft?.room_type || '—'
                                            }}</span>
                                        </div>
                                        <div class="py-2 px-3 flex-grow-1">
                                            <span class="font-weight-bold">Cabin:</span>
                                            <span class="ml-1">{{ selectedListing?.selected_code || draft?.selected_code
                                                || '—' }}</span>
                                        </div>
                                    </v-col>
                                </v-row>
                            </v-col>

                            <!-- RIGHT PRICE COLUMN (no padding; button fills height and touches edges) -->
                            <v-col cols="12" md="2" class="pa-0 d-flex">
                                <v-menu offset-y location="bottom end">
                                    <template #activator="{ props }">
                                        <v-btn v-bind="props" color="primary" variant="flat"
                                            class="w-100 h-100 text-h6 font-weight-bold price-btn"
                                            :style="{ '--v-btn-height': '100%' }">
                                            Total: ${{ formatNumber(selectedListing?.total_guest_price) }} USD
                                            <v-icon end class="ml-1">mdi-menu-down</v-icon>
                                        </v-btn>
                                    </template>

                                    <v-card min-width="260">
                                        <v-card-text class="text-subtitle-1">
                                            <div><strong>Cruise Fare:</strong> ${{
                                                formatNumber(selectedListing?.cruise_fare) }}</div>
                                            <div><strong>Taxes:</strong> ${{ formatNumber(selectedListing?.taxes) }}
                                            </div>
                                            <div><strong>Service Fee:</strong> ${{
                                                formatNumber(selectedListing?.platform_fee) }}</div>
                                            <v-divider class="my-2" />
                                            <div class="text-error font-weight-bold">
                                                Total Price: ${{ formatNumber(selectedListing?.total_guest_price) }}
                                            </div>
                                        </v-card-text>
                                    </v-card>
                                </v-menu>
                            </v-col>
                        </v-row>
                    </v-sheet>
                </v-col>

                <!-- Sign in prompt / prefill banner -->
                <v-alert v-if="!isLoggedIn" type="info" variant="tonal" class="mb-4" border="start">
                    <div class="d-flex align-center justify-space-between flex-wrap ga-2">
                        <div>Have an account? <strong>Sign in</strong> to prefill your info and save your progress.
                        </div>
                        <div class="d-flex ga-2">
                            <v-btn variant="tonal" @click="openAuth('login')">Sign In</v-btn>
                            <v-btn color="primary" @click="openAuth('signup')">Create Account</v-btn>
                        </div>
                    </div>
                </v-alert>

                <!-- PAGE-LEVEL STEPS -->
                <v-expansion-panels v-model="step" variant="accordion" class="mb-8">
                    <!-- STEP 1: GUEST INFO -->
                    <v-expansion-panel :disabled="false">
                        <v-expansion-panel-title class="d-flex align-center">
                            <v-chip label color="primary" class="mr-4">1</v-chip>
                            Guest Info
                            <v-spacer />
                            <v-chip v-if="!allGuestsComplete" size="small" color="red" label>Incomplete</v-chip>
                            <v-chip v-else size="small" color="success" label>Complete</v-chip>
                        </v-expansion-panel-title>

                        <v-expansion-panel-text>
                            <v-col cols="12" class="mt-6">
                                <v-row v-for="(guest, index) in guests" :key="index" class="mb-6">
                                    <v-col cols="12">
                                        <v-card elevation="1">
                                            <v-card-title class="d-flex align-center">
                                                <span class="text-subtitle-1 font-weight-bold">Guest {{ index + 1
                                                    }}</span>
                                                <v-chip v-if="!guestIsComplete(guest)" size="small" color="red"
                                                    class="ms-3" label>
                                                    Incomplete
                                                </v-chip>
                                            </v-card-title>

                                            <v-card-text>
                                                <v-row dense>
                                                    <v-col cols="12" md="3">
                                                        <v-select v-model="guest.title"
                                                            :items="['Dr', 'Master', 'Miss', 'Mr', 'Mrs', 'Ms']"
                                                            label="Title" variant="outlined" density="comfortable"
                                                            clearable />
                                                    </v-col>
                                                    <v-col cols="12" md="3">
                                                        <v-text-field v-model="guest.firstName" :rules="[r.required]"
                                                            label="First Name" variant="outlined"
                                                            density="comfortable" />
                                                    </v-col>
                                                    <v-col cols="12" md="3">
                                                        <v-text-field v-model="guest.middleName" label="Middle Name"
                                                            variant="outlined" density="comfortable" />
                                                    </v-col>
                                                    <v-col cols="12" md="3">
                                                        <v-text-field v-model="guest.lastName" :rules="[r.required]"
                                                            label="Last Name" variant="outlined"
                                                            density="comfortable" />
                                                    </v-col>

                                                    <v-col cols="12" md="3">
                                                        <div class="text-caption mb-1">Gender</div>
                                                        <v-btn-toggle v-model="guest.gender" color="primary" mandatory
                                                            divided class="w-100">
                                                            <v-btn value="Male" text="Male" />
                                                            <v-btn value="Female" text="Female" />
                                                        </v-btn-toggle>
                                                        <v-messages v-if="!guest.gender" class="mt-1 text-error"
                                                            :value="['Required']" />
                                                    </v-col>

                                                    <v-col cols="12" md="3">
                                                        <v-text-field v-model="guest.dob" :rules="[r.required]"
                                                            label="Date of Birth" type="date" variant="outlined"
                                                            density="comfortable" />
                                                    </v-col>
                                                    <v-col cols="12" md="3">
                                                        <v-combobox v-model="guest.citizenship" :items="countryOptions"
                                                            label="Citizenship" variant="outlined" density="comfortable"
                                                            clearable
                                                            @update:model-value="val => addOptionIfNew('countryOptions', val)" />
                                                    </v-col>

                                                    <v-col cols="12" md="3">
                                                        <v-combobox v-model="guest.language" :items="languageOptions"
                                                            label="Language (optional)" variant="outlined"
                                                            density="comfortable" clearable
                                                            @update:model-value="val => addOptionIfNew('languageOptions', val)" />
                                                    </v-col>


                                                    <v-col cols="12" md="3">
                                                        <v-text-field v-model="guest.loyalty"
                                                            label="Loyalty Number (optional)" variant="outlined"
                                                            density="comfortable" />
                                                    </v-col>

                                                    <!-- Promo Qualifiers -->
                                                    <v-col cols="12">
                                                        <v-expansion-panels variant="accordion">
                                                            <v-expansion-panel>
                                                                <v-expansion-panel-title>
                                                                    <v-icon icon="mdi-tag-multiple-outline"
                                                                        class="me-2" />
                                                                    Optional Qualifiers
                                                                </v-expansion-panel-title>
                                                                <v-expansion-panel-text>
                                                                    <v-row dense>
                                                                        <v-col cols="12" md="3">
                                                                            <v-text-field v-model="guest.state"
                                                                                label="State of Residency"
                                                                                variant="outlined" density="compact" />
                                                                        </v-col>
                                                                        <v-col cols="12" md="9">
                                                                            <div class="text-caption mb-2">Optional
                                                                                Qualifiers</div>
                                                                            <v-checkbox v-for="opt in additionalOptions"
                                                                                :key="opt.value"
                                                                                v-model="guest.selectedAdditional"
                                                                                :label="opt.label" :value="opt.value"
                                                                                density="compact" hide-details />
                                                                        </v-col>
                                                                    </v-row>
                                                                </v-expansion-panel-text>
                                                            </v-expansion-panel>
                                                        </v-expansion-panels>
                                                    </v-col>
                                                </v-row>
                                            </v-card-text>
                                        </v-card>
                                    </v-col>
                                </v-row>
                            </v-col>
                            <!-- ⬆️ END: your block ⬆️ -->

                            <div class="d-flex justify-end mt-4">
                                <v-btn color="primary" rounded="lg" :disabled="!allGuestsComplete" @click="step = 1">
                                    Continue to Preferences
                                </v-btn>
                            </div>
                        </v-expansion-panel-text>
                    </v-expansion-panel>

                    <!-- STEP 2: Preferences -->
                    <v-expansion-panel :disabled="!allGuestsComplete">
                        <v-expansion-panel-title>
                            <v-chip label color="primary" class="mr-4">2</v-chip>
                            Preferences
                            <v-spacer />
                            <v-chip v-if="!preferencesComplete" size="small" color="red" label>Incomplete</v-chip>
                            <v-chip v-else size="small" color="success" label>Complete</v-chip>
                        </v-expansion-panel-title>
                        <v-expansion-panel-text>
                            <!-- PREFERENCES (unchanged) -->
                            <v-card class="mb-6" elevation="1">
                                <v-card-title class="text-subtitle-1 font-weight-bold">Preferences</v-card-title>
                                <v-card-text>
                                    <v-row dense>
                                        <v-col cols="12" md="6">
                                            <v-select v-model="diningTime" :items="['Early', 'Late', 'AnyTime']"
                                                :rules="[r.required]" label="Dining Time" variant="outlined"
                                                density="comfortable" clearable />
                                        </v-col>
                                        <v-col cols="12" md="6">
                                            <v-select v-model="bed" :items="['Together', 'Apart']" :rules="[r.required]"
                                                label="Bed Configuration" variant="outlined" density="comfortable"
                                                clearable />
                                        </v-col>
                                    </v-row>
                                </v-card-text>
                            </v-card>

                            <div class="d-flex justify-space-between">
                                <v-btn variant="tonal" rounded="lg" @click="step = 0">Back</v-btn>
                                <v-btn color="primary" rounded="lg" :disabled="!preferencesComplete" @click="step = 2">
                                    Continue to Contact
                                </v-btn>
                            </div>
                        </v-expansion-panel-text>
                    </v-expansion-panel>

                    <!-- STEP 3: Contact -->
                    <v-expansion-panel :disabled="!preferencesComplete">
                        <v-expansion-panel-title><v-chip label color="primary" class="mr-4">3</v-chip>Contact
                            <v-spacer />
                            <v-chip v-if="!contactComplete" size="small" color="red" label>Incomplete</v-chip>
                            <v-chip v-else size="small" color="success" label>Complete</v-chip>
                        </v-expansion-panel-title>
                        <v-expansion-panel-text>
                            <!-- CONTACT (unchanged) -->
                            <v-card class="mb-6" elevation="1">
                                <v-card-title class="text-subtitle-1 font-weight-bold">Contact
                                    Information</v-card-title>
                                <v-card-text>
                                    <v-row dense>
                                        <v-col cols="12" md="6">
                                            <v-text-field v-model="contactPhone" :rules="[r.required, r.phone]"
                                                label="Phone Number" type="tel" variant="outlined"
                                                density="comfortable" />
                                        </v-col>
                                        <v-col cols="12" md="6">
                                            <v-text-field v-model="contactEmail" :rules="[r.required, r.email]"
                                                label="Email Address" type="email" variant="outlined"
                                                density="comfortable" />
                                        </v-col>
                                    </v-row>
                                </v-card-text>
                            </v-card>

                            <v-alert v-if="!isLoggedIn" type="error" variant="tonal" class="mb-4" border="start">
                                <div class="d-flex align-center justify-space-between flex-wrap ga-2">
                                    <div>You need to have an account to book this deal
                                    </div>
                                    <div class="d-flex ga-2">
                                        <v-btn color="secondary" @click="openAuth('login')">Sign In</v-btn>
                                        <v-btn color="primary" @click="openAuth('signup')">Create Account</v-btn>
                                    </div>
                                </div>
                            </v-alert>

                            <div class="d-flex justify-space-between">
                                <v-btn variant="tonal" rounded="lg" @click="step = 1">Back</v-btn>
                                <v-btn color="primary" rounded="lg" :disabled="!contactComplete || !isLoggedIn"
                                    @click="step = 3">
                                    Continue to Payment
                                </v-btn>
                            </div>
                        </v-expansion-panel-text>
                    </v-expansion-panel>

                    <!-- STEP 4: Payment / Checkout -->
                    <v-expansion-panel :disabled="!contactComplete">
                        <v-expansion-panel-title><v-chip label color="primary" class="mr-4">4</v-chip>Payment
                            <v-spacer />
                            <v-chip v-if="!paymentValid" size="small" color="red" label>Incomplete</v-chip>
                            <v-chip v-else size="small" color="success" label>Complete</v-chip>
                        </v-expansion-panel-title>
                        <v-expansion-panel-text>
                            <!-- your payment form goes here -->
                            <div class="d-flex justify-space-between">
                                <v-btn variant="tonal" rounded="lg" @click="step = 2">Back</v-btn>
                                <v-btn color="primary" rounded="lg" :disabled="!paymentValid || submitting"
                                    :loading="submitting" @click="submitCheckout">
                                    Review & Confirm
                                </v-btn>
                            </div>
                        </v-expansion-panel-text>
                    </v-expansion-panel>
                </v-expansion-panels>


                <!-- Nav buttons -->
                <v-col cols="12" class="d-flex justify-space-between">
                    <v-btn variant="tonal" rounded="lg" @click="goBack">Back</v-btn>
                </v-col>
            </v-col>
        </v-row>

        <!-- Slide-in overlay drawer -->
        <v-navigation-drawer v-model="showPorts" location="left" temporary width="580" scrim class="ports-drawer">
            <div class="d-flex align-center justify-space-between pa-3">
                <div class="text-h5 font-weight-bold text-center">Itinerary & Ports</div>
                <v-btn icon @click="showPorts = false"><v-icon>mdi-close</v-icon></v-btn>
            </div>
            <v-divider />
            <div class="pa-3">
                <PortStopsCard :foundItinerary="selectedListing?.itinerary" />
            </div>
        </v-navigation-drawer>

        <!-- Auth dialog (no redirect) -->
        <v-dialog v-model="authDialog" max-width="520">
            <v-card>
                <v-card-title class="text-h6 font-weight-bold">
                    {{ authTab === 'login' ? 'Sign In' : 'Create Account' }}
                </v-card-title>
                <v-divider />
                <v-card-text>
                    <v-alert v-if="authMsg" :type="authMsgType" variant="tonal" class="mb-4">{{ authMsg }}</v-alert>

                    <div class="d-flex ga-2 mb-4">
                        <v-btn-toggle v-model="authTab" mandatory rounded="lg" class="w-100">
                            <v-btn value="login" class="flex-1">Sign In</v-btn>
                            <v-btn value="signup" class="flex-1">Sign Up</v-btn>
                        </v-btn-toggle>
                    </div>

                    <!-- LOGIN -->
                    <v-form v-if="authTab === 'login'" @submit.prevent="signIn">
                        <v-text-field v-model="login.email" label="Email" type="email" variant="outlined"
                            density="comfortable" required class="mb-3" />
                        <v-text-field v-model="login.password" label="Password" type="password" variant="outlined"
                            density="comfortable" required />
                    </v-form>

                    <!-- SIGNUP -->
                    <v-form v-else @submit.prevent="signUp">
                        <v-row dense>
                            <v-col cols="12" md="6">
                                <v-text-field v-model="signup.firstName" label="First Name" variant="outlined"
                                    density="comfortable" />
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-text-field v-model="signup.lastName" label="Last Name" variant="outlined"
                                    density="comfortable" />
                            </v-col>
                        </v-row>
                        <v-row dense>
                            <v-col cols="12" md="7">
                                <v-text-field v-model="signup.email" label="Email" type="email" variant="outlined"
                                    density="comfortable" />
                            </v-col>
                            <v-col cols="12" md="5">
                                <v-text-field v-model="signup.phone" label="Phone (optional)" variant="outlined"
                                    density="comfortable" />
                            </v-col>
                        </v-row>
                        <v-row dense>
                            <v-col cols="12" md="6">
                                <v-text-field v-model="signup.password" label="Password" type="password"
                                    variant="outlined" density="comfortable" />
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-text-field v-model="signup.confirm" label="Confirm Password" type="password"
                                    variant="outlined" density="comfortable" />
                            </v-col>
                        </v-row>
                        <small class="text-medium-emphasis">
                            You’ll receive a confirmation email before you can finish.
                        </small>
                    </v-form>
                </v-card-text>

                <v-divider />
                <v-card-actions class="justify-end">
                    <v-btn variant="text" @click="authDialog = false" :disabled="authBusy">Close</v-btn>
                    <v-btn v-if="authTab === 'login'" color="primary" :loading="authBusy" :disabled="authBusy"
                        @click="signIn">Sign In</v-btn>
                    <v-btn v-else color="primary" :loading="authBusy" :disabled="authBusy" @click="signUp">Create
                        Account</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

    </v-container>
</template>

<script>
import { supabase } from '@/supabaseClient'
import BookingStepper from '@/components/BookingStepper.vue'
import FullPageLoader from '@/components/FullPageLoader.vue'
import PortStopsCard from '@/components/PortStopsCard.vue'
import { useNotifier } from '@/composables/useNotifier' // adjust path if needed

export default {
    name: 'BookingGuests',
    components: { BookingStepper, FullPageLoader, PortStopsCard },
    setup() {
        const { sendNotification } = useNotifier()
        return { sendNotification }
    },
    data() {
        return {
            isLoading: true,
            showPorts: false,
            cruiseId: null,
            cruise: null,
            selectedListing: null,
            draft: null,
            guests: [],
            additionalOptions: [
                { label: 'Military', value: 'military' },
                { label: 'Teacher', value: 'teacher' },
            ],
            r: {
                required: v => !!v || 'Required',
                email: v => /.+@.+\..+/.test(String(v || '')) || 'Invalid email',
                phone: v => /^\+?[\d\s().-]{7,}$/.test(String(v || '')) || 'Invalid phone',
            },
            step: 0, // 0=Guest Info, 1=Preferences, 2=Contact, 3=Payment
            diningTime: null,
            bed: null,
            contactPhone: '',
            contactEmail: '',
            paymentValid: true,
            // Starter lists (trim or expand as you like)
            countryOptions: [
                'United States', 'Canada', 'Mexico', 'United Kingdom', 'Australia',
                'Germany', 'France', 'Italy', 'Spain', 'Brazil', 'India', 'China', 'Japan'
            ],
            languageOptions: [
                'English', 'Spanish', 'French', 'German', 'Italian',
                'Portuguese', 'Chinese', 'Japanese', 'Korean', 'Arabic', 'Hindi'
            ],

            authDialog: false,
            authTab: 'login',     // 'login' | 'signup'
            authBusy: false,
            authMsg: '',
            authMsgType: 'info',  // 'info' | 'success' | 'error' | 'warning'
            login: { email: '', password: '' },
            signup: { firstName: '', lastName: '', email: '', phone: '', password: '', confirm: '' },

            submitting: false,
            snackbar: { show: false, color: 'success', msg: '' },
        }
    },
    watch: {
        isLoggedIn(val) {
            if (val) {
                this.authDialog = false
                this.prefillFromUser()
            }
        },
    },
    computed: {
        nights() {
            const t = this.selectedListing?.itinerary.title ?? ''
            const m = t.match(/(\d+)\s*days?/i)
            return m ? parseInt(m[1], 10) : null
        },
        nightsText() {
            const n = Number(this.nights)
            if (!n) return '—'
            return `${n} Night${n === 1 ? '' : '(s)'}`
        },
        preferencesComplete() {
            return !!(this.diningTime && this.bed);
        },
        contactComplete() {
            return !!(this.contactPhone && this.contactEmail && this.r.email(this.contactEmail) === true && this.r.phone(this.contactPhone) === true);
        },
        allGuestsComplete() {
            return this.guests.length > 0 && this.guests.every(g => this.guestIsComplete(g))
        },
        startDateObj() {
            const isoStr = this.selectedListing?.itinerary.date ?? null
            if (!isoStr) return null

            const [year, month, day] = isoStr.split('-').map(Number);
            return new Date(year, month - 1, day); // month is 0-based
        },
        endDateObj() {
            if (!this.startDateObj || !this.nights) return null;
            const end = new Date(this.startDateObj);
            end.setDate(end.getDate() + this.nights);
            return end;
        },
        isLoggedIn() {
            return !!this.$store?.state?.user
        },
    },
    async created() {
        this.cruiseId = this.$route.params.id
        this.listingId = this.$route.params.listing

        this.draft = this.$route.state?.bookingDraft
            || JSON.parse(sessionStorage.getItem('bookingDraft') || 'null')

        // If no draft, bounce
        if (!this.draft) {
            this.$router.replace({ name: 'DealDetail', params: { id: this.cruiseId } })
            return
        }

        // hydrate guests based on previous pages
        const totalGuests = Number(this.draft.guestsMeta.adults || 0) + Number(this.draft.guestsMeta.children || 0)
        this.guests = Array.from({ length: Math.max(totalGuests, 1) }, () => ({
            title: '', firstName: '', middleName: '', lastName: '',
            gender: '', dob: '', citizenship: '', language: '',
            loyalty: '', state: '', selectedAdditional: []
        }))

        await Promise.all([this.fetchCruise(), this.fetchSelectedListing()])

        if (this.isLoggedIn)
            this.prefillFromUser()

        this.isLoading = false

    },
    methods: {
        async fetchCruise() {
            const { data } = await supabase
                .from('cruises')
                .select('*')
                .eq('id', this.cruiseId)
                .single()
            this.cruise = data || null
        },
        async fetchSelectedListing() {
            // If you saved chosen listing ID in draft, fetch it
            if (!this.listingId) return
            const { data } = await supabase
                .from('listings')
                .select('*')
                .eq('id', this.listingId)
                .single()
            this.selectedListing = data || null
        },

        guestIsComplete(g) {
            return !!(g.firstName && g.lastName && g.gender && g.dob && g.citizenship)
        },

        parseSailing(dtStr, year) {
            if (!dtStr) return null;
            const m = String(dtStr).trim().match(/^(\d{1,2})\s+([A-Za-z]{3})\s+(\d{1,2}):(\d{2})$/);
            if (!m) return null;

            const [, dd, monStr, hh, mm] = m;
            const months = { Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5, Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11 };
            const mon = months[monStr];
            const yr = Number.isInteger(year) ? year : new Date().getFullYear();

            const d = new Date(yr, mon, Number(dd), Number(hh), Number(mm), 0, 0);
            return isNaN(d.getTime()) ? null : d;
        },

        formatSailingTime(dtStr, year) {
            const d = this.parseSailing(dtStr, year);
            if (!d) return '—';
            return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
        },

        formatSailingDate(dateObj) {
            if (!(dateObj instanceof Date) || isNaN(dateObj)) return '—';
            return dateObj.toLocaleDateString('en-US', {
                month: 'short',
                day: '2-digit',
                year: 'numeric'
            });
        },
        formatNumber(val) {
            if (val == null) return 'TBD'
            return Number(val).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        },

        goBack() {
            this.$router.back()
        },
        goNext() {
            // persist guest details back to draft
            const nextDraft = { ...this.draft, guests: this.guests }
            sessionStorage.setItem('bookingDraft', JSON.stringify(nextDraft))
            // push to next step (checkout)
            this.$router.push({ name: 'BookingCheckout', params: { id: this.cruiseId }, state: { bookingDraft: nextDraft } })
        },
        genBookingCode(len = 8) {
            const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
            let out = '';
            for (let i = 0; i < len; i++) out += chars[Math.floor(Math.random() * chars.length)];
            return out;
        },
        toIsoDate(d) {
            if (!d) return null;
            // keep yyyy-mm-dd; DB is fine with date text
            return String(d).slice(0, 10);
        },
        normalizeGuest(g = {}) {
            return {
                title: g.title || null,
                firstName: (g.firstName || '').trim() || null,
                middleName: (g.middleName || '').trim() || null,
                lastName: (g.lastName || '').trim() || null,
                gender: g.gender || null,
                dob: this.toIsoDate(g.dob),
                citizenship: g.citizenship || null,
                language: g.language || null,
                loyalty: g.loyalty || null,
                state: g.state || null,
                selectedAdditional: Array.isArray(g.selectedAdditional) ? g.selectedAdditional : []
            };
        },

        toast(msg, type = 'success') {
            this.snackbar.msg = msg
            this.snackbar.color = type === 'error' ? 'error' : type
            this.snackbar.show = true
        },

        async submitCheckout() {
            if (this.submitting) return
            this.submitting = true
            try {
                const nextDraft = { ...this.draft, guests: this.guests }
                sessionStorage.setItem('bookingDraft', JSON.stringify(nextDraft))

                const { data: { session }, error: sessionError } = await supabase.auth.getSession()
                if (sessionError || !session?.user) {
                    throw new Error('Please sign in to complete your booking.')
                }

                const guestsJson = {
                    meta: nextDraft?.guestsMeta || { adults: 0, children: 0, hasSenior: false },
                    residency: nextDraft?.residency || { country: 'US' },
                    list: (nextDraft?.guests || []).map(this.normalizeGuest)
                }

                const booking_id = this.genBookingCode(8)
                const bookingPayload = {
                    user_id: session.user.id,
                    listing_id: this.selectedListing.id,
                    guests: guestsJson,
                    dining_time: this.diningTime,
                    bed: this.bed,
                    phone: this.contactPhone,
                    email: this.contactEmail,
                    booking_id
                }

                const { error: insertError } = await supabase.from('bookings').insert([bookingPayload])
                if (insertError) throw insertError

                await supabase.from('listings')
                    .update({ current_step: 2 })
                    .eq('id', this.selectedListing.id)

                // Optional notification, if wired
                try {
                    await this.sendNotification?.({
                        type: 'booking_requested',
                        forAdmin: false,
                        listingId: this.selectedListing.id
                    })
                } catch (e) {
                    console.warn('Notification failed:', e)
                }

                this.$emit?.('submitted')
                this.toast('Booking submitted successfully!', 'success')

                // short delay so the user sees the snackbar
                setTimeout(() => {
                    const isAgent = !!this.$store?.state?.user?.is_agent
                    const target = isAgent ? '/dashboard/home' : '/dashboard/bookings'
                    this.$router.replace(target)
                }, 600)
            } catch (e) {
                console.error('submitCheckout failed:', e)
                this.toast(e?.message || 'Could not submit booking. Please try again.', 'error')
            } finally {
                this.submitting = false
            }
        },
        addOptionIfNew(listName, val) {
            if (!val) return
            const list = this[listName]
            if (Array.isArray(list) && !list.includes(val)) list.push(val)
        },

        openAuth(tab = 'login') {
            this.authTab = tab
            this.authMsg = ''
            this.authDialog = true

            if (tab != 'login') {
                this.signup.firstName = this.guests[0].firstName
                this.signup.lastName = this.guests[0].lastName
                this.signup.email = this.contactEmail
                this.signup.phone = this.contactPhone
            }
        },

        async signIn() {
            if (this.authBusy) return
            this.authBusy = true
            this.authMsg = ''
            try {
                const { error } = await supabase.auth.signInWithPassword({
                    email: this.login.email,
                    password: this.login.password,
                })
                if (error) throw error

                // hydrate Vuex user
                await this.$store.dispatch('fetchUserData')
                this.prefillFromUser()
                this.authMsgType = 'success'
                this.authMsg = 'Signed in!'
                // small pause so user sees success, then close
                setTimeout(() => { this.authDialog = false }, 300)
            } catch (e) {
                this.authMsgType = 'error'
                this.authMsg = e?.message || 'Sign in failed.'
            } finally {
                this.authBusy = false
            }
        },

        async signUp() {
            if (this.authBusy) return
            this.authBusy = true
            this.authMsg = ''
            try {
                if (!this.signup.password || this.signup.password !== this.signup.confirm) {
                    throw new Error('Passwords do not match.')
                }
                const { data, error } = await supabase.auth.signUp({
                    email: this.signup.email,
                    password: this.signup.password,
                    options: {
                        emailRedirectTo: `${window.location.origin}/#/`,
                        data: {
                            firstName: this.signup.firstName,
                            lastName: this.signup.lastName,
                            phone: this.signup.phone,
                            isAdmin: false,
                        },
                    },
                })
                if (error) throw error

                // insert into users table (your existing code)
                const user = data.user
                if (user?.id) {
                    await supabase.from('users').insert([{
                        uid: user.id,
                        email: this.signup.email,
                        first_name: this.signup.firstName,
                        last_name: this.signup.lastName,
                        phone: this.signup.phone,
                        created_at: new Date().toISOString()
                    }])
                }

                // With email confirmation ON, session is usually null
                if (!data.session) {
                    this.authMsgType = 'success'
                    this.authMsg = 'Account created—check your email to confirm, then come back here to login'
                } else {
                    // If you disabled confirmation, user is logged in now:
                    await this.$store.dispatch('fetchUserData')
                    this.prefillFromUser()
                    this.authDialog = false
                }
            } catch (e) {
                this.authMsgType = 'error'
                this.authMsg = e?.message || 'Sign up failed.'
            } finally {
                this.authBusy = false
            }
        },
        prefillFromUser() {
            const u = this.$store?.state?.user
            if (!u) return
            // Example prefill mappings (only if present in your schema)
            this.contactEmail = u.email
            this.contactPhone = u.phone || this.contactPhone
            this.guests[0].firstName = u.first_name
            this.guests[0].middleName = u.middle_name
            this.guests[0].lastName = u.last_name
            this.guests[0].gender = u.gender
            this.guests[0].citizenship = u.citizenship
            this.guests[0].language = u.language
            this.guests[0].dob = u.dob
        },
    }
}
</script>

<style scoped>
.summary-header {
    background: #fff;
}

.timeline-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: rgba(0, 0, 0, .3);
}

.timeline-line {
    height: 2px;
    flex: 1 1 auto;
    background: rgba(0, 0, 0, .2);
    max-width: 140px;
}

.info-strip {
    border: 1px solid rgba(0, 0, 0, .08);
}

/* Button fills the price column and matches the sheet corners on the right */
.price-btn {
    border-radius: 0;
    border-top-right-radius: inherit;
    border-bottom-right-radius: inherit;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 20px;
    /* room for the chevron */
}

@media (min-width: 960px) {
    .border-right-md {
        border-right: 1px solid rgba(0, 0, 0, .08);
    }
}
</style>
